import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Provider } from 'react-redux';
import { createChordClient } from '@chordcommerce/chord-js-autonomy';
import createStore from '../redux/store';
import { ChordClientContext } from '../hooks/use-chord';
import { ChordAuthClientContext } from '../hooks/use-auth';
import { deepEqual } from '../utils';
/**
 * React context provider for Chord services. This component is mainly a wrapper
 * for a [Redux](https://redux.js.org/) store that powers Chord's analytics,
 * cart, checkout, subscription and user models.
 *
 * @example
 *
 * ```javascript
 * import ChordProvider from '@chordcommerce/react-autonomy'
 * import { StytchClient } from '@chordcommerce/chord-stytch'
 * import { StytchProvider } from '@stytch/nextjs'
 *
 * const App = ({ children }) => {
 *   const authClient = new StytchClient({
 *     apiKey: process.env.STYTCH_API_KEY,
 *   })
 *
 *   return (
 *     <ChordProvider
 *       config={{
 *         domain: process.env.CHORD_OMS_DOMAIN,
 *         brandName: process.env.CHORD_OMS_BRAND_NAME,
 *         storeId: process.env.CHORD_OMS_STORE_ID,
 *         omsId: process.env.CHORD_OMS_ID,
 *         tenantId: process.env.CHORD_OMS_TENANT_ID,
 *         storeSlug: process.env.CHORD_OMS_STORE_SLUG,
 *         productUrlFormatter: (product) => {
 *           return product.url || `https://example.com/${product.variant?.slug}`
 *         }
 *       }}
 *       authClient={authClient}
 *     >
 *       <StytchProvider stytch={authClient.getServiceClient()}>
 *         {children}
 *       </StytchProvider>
 *     </ChordProvider>
 *   )
 * }
 * ```
 */
var ChordProvider = function ChordProvider(_ref) {
  var children = _ref.children,
    config = _ref.config,
    middleware = _ref.middleware,
    preloadedState = _ref.preloadedState,
    reducer = _ref.reducer,
    auth = _ref.auth;
  var _useState = useState(config),
    _useState2 = _slicedToArray(_useState, 2),
    configCache = _useState2[0],
    setConfigCache = _useState2[1];
  useEffect(function () {
    try {
      if (!deepEqual(config, configCache)) {
        setConfigCache(config);
      }
    } catch (e) {
      console.error(e);
    }
  }, [config]);
  if (!config) {
    throw new Error('ChordProvider: Missing required attribute `config`');
  }
  var chordClient = useMemo(function () {
    return createChordClient(configCache);
  }, [configCache]);
  // Memoize the Redux store and keep the same, regardless of whether or not the
  // config changes. In the past, if we let it re-initialize because the
  // `productUrlFormatter` or some object based value changed, the application
  // would begin to behave erratically. As such, we must keep the dependency
  // array empty.
  var store = useMemo(function () {
    return createStore({
      reducer: reducer,
      preloadedState: preloadedState,
      middleware: middleware,
      config: configCache,
      auth: auth
    });
  }, []);
  return _jsx(Provider, {
    store: store,
    children: _jsx(ChordClientContext.Provider, {
      value: chordClient,
      children: _jsx(ChordAuthClientContext.Provider, {
        value: auth,
        children: children
      })
    })
  });
};
export default ChordProvider;